import Big from 'big.js';

import { ensureBig } from './big-conversion';

/**
 * Convert rate number to percentage string
 * rateの数値をパーセント表記の文字列に変換
 * Big型またはnumber型に対応
 *
 * @description
 * Converts a decimal rate value to a percentage string with specified precision.
 * Uses Big.js for accurate decimal arithmetic.
 *
 * 小数のレート値を指定された精度でパーセント文字列に変換します。
 * 正確な小数演算のためにBig.jsを使用します。
 *
 * @param num - Number to convert (0.01 = 1%) / 変換する数値（0.01 = 1%として扱う）
 * @param precision - Number of decimal places / 小数点以下の桁数
 * @param [trimTailingZeros=false] - Whether to remove trailing zeros / 末尾のゼロを削除するかどうか
 *
 * @returns Percentage string / パーセント表記の文字列
 *
 * @example
 * // Basic usage (keeps trailing zeros by default)
 * // 基本的な使用（デフォルトでは末尾のゼロを保持）
 * rate2Percent(0.01, 2) // "1.00"
 * rate2Percent(0.123, 2) // "12.30"
 * rate2Percent(0.5, 1) // "50.0"
 *
 * @example
 * // Remove trailing zeros (trimTailingZeros=true)
 * // 末尾のゼロを削除（trimTailingZeros=true）
 * rate2Percent(0.01, 2, true) // "1"
 * rate2Percent(0.123, 2, true) // "12.3"
 * rate2Percent(0.5, 1, true) // "50"
 * rate2Percent(0.125, 1, true) // "12.5"
 */
export function rate2Percent(value: Big | number, precision: number, trimTailingZeros: boolean = false): string {
  const big = ensureBig(value);
  let str = big.times(100).toFixed(precision);
  if (trimTailingZeros && str.includes('.')) {
    // 小数点がある場合のみ、末尾のゼロを削除し、必要に応じて小数点も削除
    // 例: "1.2300" -> "1.23"
    // 例: "120.00" -> "120"
    // 例: "12.0" -> "12"
    // 正規表現の説明:
    // \.0+$ - 小数点の後が全て0の場合（例: ".00"）
    // (\.\d*?)0+$ - 小数点以下の末尾の0を削除（例: ".1200" → ".12"）
    str = str.replace(/\.0+$|(\.\d*?)0+$/, '$1');
  }
  return str;
}
